import { CSSProperties, FC, HTMLAttributes } from 'react'

import Tooltip from 'src/components/ui/atoms/Tooltip'
import { cn } from 'src/lib/cn'
import { cva, VariantProps } from 'src/lib/cva'
import { truncate } from 'src/utils/text'

export const BADGE_TYPE = {
  success: 'success',
  danger: 'danger',
  notification: 'notification',
  notificationMenu: 'notificationMenu',
  secondary: 'secondary',
  neutral: 'neutral',
}

const BadgeVariant = cva(
  'radius-sm flex items-center justify-center px-1 py-0.5 text-[10px] text-white uppercase',
  {
    variants: {
      type: {
        [BADGE_TYPE.success]: 'bg-green',
        [BADGE_TYPE.danger]: 'bg-red',
        [BADGE_TYPE.notification]: 'bg-blue',
        [BADGE_TYPE.notificationMenu]:
          'bg-[#ececec] text-[#9eabb0] text-[8px] rounded-[1px] leading-[1] h-[11.2px] px-[.1rem]',
        [BADGE_TYPE.neutral]: 'bg-grey radius-3',
      },
      small: {
        true: 'px-0.2 py-0.1 text-xs',
      },
      unknown: {
        true: 'text-dark-grey text-sm capitalize font-semibold',
      },
    },
  },
)

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  label: any
  type?: string
  color?: string
  textColor?: string
  small?: boolean
  className?: string
  truncLimit?: number
}

const Badge: FC<BadgeProps & VariantProps<typeof BadgeVariant>> = ({
  label,
  type,
  small,
  color,
  textColor,
  className,
  truncLimit,
  ...otherProps
}: BadgeProps) => {
  const shouldLabelBeTruncate = label.length > (truncLimit as number)
  const truncLabel = shouldLabelBeTruncate ? truncate(label, truncLimit as number) : label

  const style = {
    backgroundColor: label === 'unknown' ? 'unset' : color,
  } as CSSProperties

  if (textColor) {
    style.color = textColor
  }

  return (
    <>
      <span
        data-testid="ecosystem-badge"
        style={style}
        className={cn(BadgeVariant({ type, small, unknown: label === 'unknown', className }))}
        data-tip=""
        data-for={label}
        {...otherProps}
      >
        {truncLabel}
      </span>
      {shouldLabelBeTruncate && (
        <Tooltip id={label} delayShow={300}>
          {label}
        </Tooltip>
      )}
    </>
  )
}

export default Badge
